import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { palette, admin, transition, settingsHub, fontFamily } from '../../../styles/tokens';
import type { SettingsCardConfig } from '../config/settingsConfig';
import type { CompletionStatus } from '../hooks/useSettingsCompletion';

interface SettingsCardProps {
  config: SettingsCardConfig;
  status?: CompletionStatus;
}

export function SettingsCard({ config, status }: SettingsCardProps) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const Icon = config.icon;

  const showBadge = status === 'done' || status === 'needs_setup';

  return (
    <button
      onClick={() => navigate(config.route)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '16px 18px',
        background: palette.white,
        border: `0.5px solid ${hovered ? admin.borderHover : palette.g200}`,
        borderRadius: admin.cardRadius,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily,
        transition: `border-color ${transition.fast}`,
        position: 'relative',
      }}
    >
      {/* Header row: icon + title + badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: config.iconColors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={16} strokeWidth={1.8} color={config.iconColors.fg} />
        </div>
        <span style={{
          fontSize: 14,
          fontWeight: 500,
          color: palette.g800,
          flex: 1,
        }}>
          {config.title}
        </span>
        {showBadge && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 10,
            background: status === 'done' ? settingsHub.badgeDoneBg : settingsHub.badgeSetupBg,
            color: status === 'done' ? settingsHub.badgeDoneFg : settingsHub.badgeSetupFg,
            whiteSpace: 'nowrap',
          }}>
            {status === 'done' ? 'Done' : 'Set up'}
          </span>
        )}
      </div>

      {/* Description */}
      <div style={{
        fontSize: 12,
        color: palette.g500,
        lineHeight: 1.4,
        paddingLeft: 42,
      }}>
        {config.description}
      </div>
    </button>
  );
}
